/**
 * capacityTelemetryApi.ts
 * Feature: 154 - Eventstream Real-Time Capacity Telemetry
 * Author: Peter Dobler
 * Date: 2026-05-18
 * Constitution Version: 1.12.0
 *
 * API client for /api/capacity-telemetry/* endpoints.
 * All requests use the centralized apiClient for auth interceptors.
 */

import { apiClient } from './apiClient';
import type {
  LiveTelemetryResponse,
  CapacityHistoryResponse,
  ThrottlingForecast,
  TelemetryServiceStatus,
  CapacityTelemetryPipeline,
  UpsertPipelineRequest,
  PipelineValidationResult,
  StreamingAlertRule,
  StreamingAlertRulesResponse,
  CreateStreamingAlertRuleRequest,
} from '@shared/types/capacityTelemetry';

const BASE = '/api/capacity-telemetry';

// ---------------------------------------------------------------------------
// Live / status (US1)
// ---------------------------------------------------------------------------

export interface LiveQueryParams {
  capacityType?: string;
  clientId?: string;
}

export async function fetchLiveMetrics(
  params?: LiveQueryParams
): Promise<LiveTelemetryResponse> {
  const query = new URLSearchParams();
  if (params?.capacityType) query.set('capacityType', params.capacityType);
  if (params?.clientId) query.set('clientId', params.clientId);
  const qs = query.toString();
  const { data } = await apiClient.get<LiveTelemetryResponse>(
    `${BASE}/live${qs ? `?${qs}` : ''}`
  );
  return data;
}

export async function fetchTelemetryStatus(): Promise<TelemetryServiceStatus> {
  const { data } = await apiClient.get<TelemetryServiceStatus>(`${BASE}/status`);
  return data;
}

// ---------------------------------------------------------------------------
// Capacity history (US2)
// ---------------------------------------------------------------------------

export async function fetchCapacityHistory(
  capacityId: string,
  windowMinutes = 30
): Promise<CapacityHistoryResponse> {
  const { data } = await apiClient.get<CapacityHistoryResponse>(
    `${BASE}/capacity/${capacityId}?windowMinutes=${windowMinutes}`
  );
  return data;
}

// ---------------------------------------------------------------------------



// ---------------------------------------------------------------------------
// Forecast (US4)
// ---------------------------------------------------------------------------

export async function fetchForecast(capacityId: string): Promise<ThrottlingForecast> {
  const { data } = await apiClient.get<ThrottlingForecast>(
    `${BASE}/forecast/${capacityId}`
  );
  return data;
}

// ---------------------------------------------------------------------------
// Pipeline management (Admin — US3)
// ---------------------------------------------------------------------------

export async function fetchPipelines(): Promise<CapacityTelemetryPipeline[]> {
  const { data } = await apiClient.get<{ pipelines: CapacityTelemetryPipeline[] }>(
    `${BASE}/pipelines`
  );
  return data.pipelines;
}

export async function upsertPipeline(
  capacityId: string,
  payload: UpsertPipelineRequest
): Promise<CapacityTelemetryPipeline> {
  const { data } = await apiClient.post<CapacityTelemetryPipeline>(
    `${BASE}/pipelines/${capacityId}`,
    payload
  );
  return data;
}

export async function validatePipeline(
  capacityId: string
): Promise<PipelineValidationResult> {
  const { data } = await apiClient.post<PipelineValidationResult>(
    `${BASE}/pipelines/${capacityId}/validate`,
    {}
  );
  return data;
}

// ---------------------------------------------------------------------------
// Streaming alert rules (US3 / Operator+)
// ---------------------------------------------------------------------------

export async function fetchStreamingAlertRules(
  capacityId?: string
): Promise<StreamingAlertRule[]> {
  const qs = capacityId ? `?capacityId=${capacityId}` : '';
  const { data } = await apiClient.get<StreamingAlertRulesResponse>(
    `${BASE}/alert-rules${qs}`
  );
  return data.rules;
}

export async function createStreamingAlertRule(
  payload: CreateStreamingAlertRuleRequest
): Promise<StreamingAlertRule> {
  const { data } = await apiClient.post<StreamingAlertRule>(
    `${BASE}/alert-rules`,
    payload
  );
  return data;
}
